import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { CadenceAccount, Page, WorkItem } from '../lib/types';
import {
  CADENCE_LABEL,
  CADENCE_TONE,
  HEALTH_LABEL,
  HEALTH_TONE,
  IMPORTANCE_TONE,
  PRIORITY_TONE,
  READINESS_LABEL,
  READINESS_TONE,
  WAITING_STATUS_LABEL,
  WAITING_STATUS_TONE,
  formatDate,
  relativeDays,
  titleCase,
} from '../lib/format';
import {
  Badge,
  Card,
  Checkbox,
  EmptyState,
  ErrorNotice,
  Input,
  Loading,
  PageHeader,
  SectionNote,
  Select,
  StatTile,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';
import { WorkItemDialog } from '../features/WorkItemDialog';

/**
 * Cross-account list views: Commitments, Follow-ups and Cadence.
 *
 * These are the "show me everything of one kind" screens, as distinct from My
 * Work which is "show me what to do next". Both are needed: the priority list
 * answers today, these answer the shape of the portfolio.
 */

export function CommitmentsPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [readiness, setReadiness] = useState('');
  const [externalOnly, setExternalOnly] = useState(false);
  const [escalatedOnly, setEscalatedOnly] = useState(false);
  const [q, setQ] = useState('');

  const query = useQuery({
    queryKey: ['work-items', 'commitments', { openOnly, readiness, externalOnly, escalatedOnly, q }],
    queryFn: () =>
      api.get<Page<WorkItem>>('/work-items', {
        type: 'customer_commitment',
        openOnly: openOnly || undefined,
        readiness: readiness || undefined,
        externallyCommitted: externalOnly || undefined,
        escalatedOnly: escalatedOnly || undefined,
        q: q || undefined,
        sort: 'due',
        limit: 200,
      }),
  });

  const items = query.data?.data ?? [];
  const now = new Date();
  const overdue = items.filter(
    (item) => item.status !== 'completed' && item.dueAt && new Date(item.dueAt) < now,
  ).length;
  const external = items.filter((item) => item.commitment?.externallyCommitted).length;
  const unconfirmed = items.filter((item) => item.commitment?.readiness === 'unknown').length;

  return (
    <>
      <PageHeader
        title="Commitments"
        description="Everything we owe customers, with the customer date and the internal runway kept separate."
        meta={
          <div className="flex flex-wrap items-center gap-3">
            <Input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Search commitments"
              className="w-56"
            />
            <Select
              value={readiness}
              onChange={(event) => setReadiness(event.target.value)}
              className="w-44"
            >
              <option value="">Any readiness</option>
              <option value="unknown">Not confirmed</option>
              <option value="on_track">On track</option>
              <option value="at_risk">At risk</option>
              <option value="blocked">Blocked</option>
            </Select>
            <Checkbox
              label="Open only"
              checked={openOnly}
              onChange={(event) => setOpenOnly(event.target.checked)}
            />
            <Checkbox
              label="Told the customer"
              checked={externalOnly}
              onChange={(event) => setExternalOnly(event.target.checked)}
            />
            <Checkbox
              label="Escalated"
              checked={escalatedOnly}
              onChange={(event) => setEscalatedOnly(event.target.checked)}
            />
          </div>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Showing" value={items.length} />
        <StatTile label="Overdue" value={overdue} tone="critical" />
        <StatTile
          label="Externally committed"
          value={external}
          context="Promised to a customer"
        />
        <StatTile
          label="Readiness not confirmed"
          value={unconfirmed}
          tone="warning"
          context="Nobody has checked yet"
        />
      </div>

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}
      {query.data && items.length === 0 && <EmptyState title="No commitments match" />}

      {items.length > 0 && (
        <Card padded={false}>
          <Table>
            <thead>
              <tr>
                <Th>Commitment</Th>
                <Th>Account</Th>
                <Th>Customer date</Th>
                <Th>Internal target</Th>
                <Th>Readiness</Th>
                <Th>Told customer</Th>
                <Th>Owner</Th>
                <Th align="right">Priority</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isOverdue =
                  item.status !== 'completed' && item.dueAt && new Date(item.dueAt) < now;
                return (
                  <Tr key={item.id} onClick={() => setSelected(item.id)}>
                    <Td>
                      <span className="flex items-center gap-2">
                        <span className="font-medium text-ink">{item.title}</span>
                        {item.commitment?.leadershipVisibleAt && (
                          <Badge tone="critical">Escalated</Badge>
                        )}
                        {item.commitment?.overrideWarningReason && (
                          <Badge
                            tone="warning"
                            title={item.commitment.overrideWarningReason}
                          >
                            Recorded over a warning
                          </Badge>
                        )}
                      </span>
                    </Td>
                    <Td>
                      <span className="flex items-center gap-1.5">
                        <Link
                          to={`/accounts/${item.accountId}`}
                          onClick={(event) => event.stopPropagation()}
                          className="text-accent hover:underline"
                        >
                          {item.accountName}
                        </Link>
                        {item.accountImportance === 'strategic' && (
                          <Badge tone="info" dot={false}>
                            S
                          </Badge>
                        )}
                      </span>
                    </Td>
                    <Td className={isOverdue ? 'font-medium text-critical' : undefined}>
                      {formatDate(item.commitment?.customerDueAt)}
                      <span className="mt-0.5 block text-xs text-muted">
                        {relativeDays(item.commitment?.customerDueAt)}
                      </span>
                    </Td>
                    <Td className="text-ink-2">{formatDate(item.commitment?.internalTargetAt)}</Td>
                    <Td>
                      {item.commitment && (
                        <Badge tone={READINESS_TONE[item.commitment.readiness]}>
                          {READINESS_LABEL[item.commitment.readiness]}
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      {item.commitment?.externallyCommitted ? (
                        <Badge tone="serious" dot={false}>
                          Yes
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted">Internal</span>
                      )}
                    </Td>
                    <Td className="text-xs text-ink-2">{item.ownerName}</Td>
                    <Td align="right">
                      <Badge
                        tone={PRIORITY_TONE[item.priority]}
                        dot={item.priority === 'critical'}
                      >
                        {titleCase(item.priority)}
                      </Badge>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      {selected && (
        <WorkItemDialog
          workItemId={selected}
          onClose={() => setSelected(null)}
          onChanged={() => query.refetch()}
        />
      )}
    </>
  );
}

export function FollowupsPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [waitingStatus, setWaitingStatus] = useState('');
  const [openOnly, setOpenOnly] = useState(true);

  const query = useQuery({
    queryKey: ['work-items', 'followups', { waitingStatus, openOnly }],
    queryFn: () =>
      api.get<Page<WorkItem>>('/work-items', {
        type: 'customer_followup',
        waitingStatus: waitingStatus || undefined,
        openOnly: openOnly || undefined,
        sort: 'due',
        limit: 200,
      }),
  });

  const items = query.data?.data ?? [];
  const now = new Date();
  const overdue = items.filter(
    (item) => item.followup && new Date(item.followup.followupAt) < now,
  ).length;
  const detected = items.filter(
    (item) => item.followup?.waitingStatus === 'customer_response_detected',
  ).length;

  return (
    <>
      <PageHeader
        title="Follow-ups"
        description="Everything we are waiting on customers for. Waiting on a customer is first-class work, not a note in someone's inbox."
        meta={
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={waitingStatus}
              onChange={(event) => setWaitingStatus(event.target.value)}
              className="w-52"
            >
              <option value="">Any state</option>
              <option value="waiting_on_customer">Waiting on customer</option>
              <option value="followup_due">Follow-up due</option>
              <option value="customer_response_detected">Response detected</option>
              <option value="followed_up">Followed up</option>
              <option value="resolved">Resolved</option>
            </Select>
            <Checkbox
              label="Open only"
              checked={openOnly}
              onChange={(event) => setOpenOnly(event.target.checked)}
            />
          </div>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="Showing" value={items.length} />
        <StatTile label="Follow-up overdue" value={overdue} tone="serious" />
        <StatTile
          label="Responses to review"
          value={detected}
          tone="info"
          context="Nothing closes automatically"
        />
      </div>

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}
      {query.data && items.length === 0 && <EmptyState title="Nothing outstanding" />}

      {items.length > 0 && (
        <Card padded={false}>
          <Table>
            <thead>
              <tr>
                <Th>Follow-up</Th>
                <Th>Account</Th>
                <Th>Waiting for</Th>
                <Th>Customer said</Th>
                <Th>Follow up on</Th>
                <Th>State</Th>
                <Th>Owner</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isOverdue =
                  item.followup && new Date(item.followup.followupAt) < now;
                return (
                  <Tr key={item.id} onClick={() => setSelected(item.id)}>
                    <Td className="font-medium">{item.title}</Td>
                    <Td>
                      <Link
                        to={`/accounts/${item.accountId}`}
                        onClick={(event) => event.stopPropagation()}
                        className="text-accent hover:underline"
                      >
                        {item.accountName}
                      </Link>
                    </Td>
                    <Td className="max-w-64 text-xs text-ink-2">
                      <span className="line-clamp-2">{item.followup?.waitingFor}</span>
                    </Td>
                    <Td className="text-xs text-ink-2">
                      {formatDate(item.followup?.customerExpectedAt)}
                    </Td>
                    <Td className={isOverdue ? 'font-medium text-critical' : undefined}>
                      {formatDate(item.followup?.followupAt)}
                      <span className="mt-0.5 block text-xs text-muted">
                        {relativeDays(item.followup?.followupAt)}
                      </span>
                    </Td>
                    <Td>
                      {item.followup && (
                        <Badge tone={WAITING_STATUS_TONE[item.followup.waitingStatus]}>
                          {WAITING_STATUS_LABEL[item.followup.waitingStatus]}
                        </Badge>
                      )}
                    </Td>
                    <Td className="text-xs text-ink-2">{item.ownerName}</Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      {selected && (
        <WorkItemDialog
          workItemId={selected}
          onClose={() => setSelected(null)}
          onChanged={() => query.refetch()}
        />
      )}
    </>
  );
}

export function CadencePage() {
  const query = useQuery({
    queryKey: ['cadence'],
    queryFn: () => api.get<{ accounts: CadenceAccount[]; generatedAt: string }>('/cs/cadence'),
  });

  const accounts = query.data?.accounts ?? [];
  const behind = accounts.filter((a) => a.cadence.status === 'behind').length;
  const dueSoon = accounts.filter((a) => a.cadence.status === 'due_soon').length;
  const unconfigured = accounts.filter((a) => !a.hasSuccessPlan).length;

  return (
    <>
      <PageHeader
        title="Cadence"
        description="Whether we are keeping to the contact rhythm each success plan sets out. This is a fact about us, not a rating of the customer."
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Accounts" value={accounts.length} />
        <StatTile label="Behind" value={behind} tone="critical" />
        <StatTile label="Due soon" value={dueSoon} tone="warning" />
        <StatTile
          label="No success plan"
          value={unconfigured}
          tone="neutral"
          context="Nothing to measure against"
        />
      </div>

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {accounts.length > 0 && (
        <Card padded={false}>
          <Table>
            <thead>
              <tr>
                <Th>Account</Th>
                <Th>Importance</Th>
                <Th>Health</Th>
                <Th>Cadence</Th>
                <Th>Gaps</Th>
                <Th>Primary CSM</Th>
                <Th align="right">Last activity</Th>
                <Th align="right">Last meeting</Th>
                <Th align="right">Next meeting</Th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <Tr key={account.accountId}>
                  <Td>
                    <Link
                      to={`/accounts/${account.accountId}`}
                      className="font-medium text-accent hover:underline"
                    >
                      {account.accountName}
                    </Link>
                    {!account.hasSuccessPlan && (
                      <span className="mt-0.5 block text-xs text-muted">No success plan</span>
                    )}
                  </Td>
                  <Td>
                    <Badge
                      tone={IMPORTANCE_TONE[account.accountImportance]}
                      dot={account.accountImportance === 'strategic'}
                    >
                      {titleCase(account.accountImportance)}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge tone={HEALTH_TONE[account.health]}>
                      {HEALTH_LABEL[account.health]}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge tone={CADENCE_TONE[account.cadence.status]}>
                      {CADENCE_LABEL[account.cadence.status]}
                    </Badge>
                  </Td>
                  <Td className="max-w-80">
                    {account.cadence.gaps.length === 0 ? (
                      <span className="text-xs text-muted">—</span>
                    ) : (
                      <ul className="space-y-0.5 text-xs text-ink-2">
                        {account.cadence.gaps.map((gap) => (
                          <li key={gap.kind}>{gap.summary}</li>
                        ))}
                      </ul>
                    )}
                  </Td>
                  <Td className="text-xs text-ink-2">{account.primaryCsmName}</Td>
                  <Td align="right" className="tnum text-xs whitespace-nowrap text-muted">
                    {account.cadence.daysSinceLastActivity === null
                      ? '—'
                      : `${account.cadence.daysSinceLastActivity}d`}
                  </Td>
                  <Td align="right" className="tnum text-xs whitespace-nowrap text-muted">
                    {account.cadence.daysSinceLastMeeting === null
                      ? '—'
                      : `${account.cadence.daysSinceLastMeeting}d`}
                  </Td>
                  <Td align="right" className="tnum text-xs whitespace-nowrap text-muted">
                    {account.cadence.daysUntilNextMeeting === null
                      ? 'none'
                      : `${account.cadence.daysUntilNextMeeting}d`}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <div className="mt-3">
        <SectionNote>
          Paused and closed accounts are excluded — they are not expected to receive cadence.
          Scheduled meetings come from calendar sources; once the calendar connector is live
          (Phase 4) the “next meeting” column fills itself.
        </SectionNote>
      </div>
    </>
  );
}
